/* eslint-disable no-promise-executor-return */

// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {FlatList, Space, Styled} from '@supermove/components';
import {useMemo, useCallback, useResponsive, useFlatList, memo} from '@supermove/hooks';
import {colors} from '@supermove/styles';
import {NativeScrollEvent} from '@supermove/styles/types';

// App
import Line from '@shared/modules/App/components/Line';
import ProjectDetailsAssignedStaffBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsAssignedStaffBlock';
import ProjectDetailsClientsBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsClientsBlock';
import ProjectDetailsInternalInformationBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsInternalInformationBlock';
import ProjectDetailsLeadInformationBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsLeadInformationBlock';
import ProjectDetailsOverviewBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsOverviewBlock';
import ProjectDetailsReferralInformationBlock from '@shared/modules/Project/components/widgets/components/ProjectDetailsReferralInformationBlock';
import useProjectDetailsAutoScroll from '@shared/modules/Project/components/widgets/hooks/useProjectDetailsAutoScroll';
import useProjectDetailsContext from '@shared/modules/Project/components/widgets/hooks/useProjectDetailsContext';
import {NavigationSectionType} from '@shared/modules/Project/components/widgets/types/NavigationSection';
import ProjectBlockKind, {
  ProjectBlockKindType,
} from '@shared/modules/Project/enums/ProjectBlockKind';

const BlockContainer = Styled.View`
  background-color: ${colors.white};
`;

const BlockContent = ({
  kind,
  index,
  projectUuid,
}: {
  kind: ProjectBlockKindType;
  index: number;
  projectUuid: string;
}) => {
  switch (kind) {
    case ProjectBlockKind.PROJECT_OVERVIEW:
      return <ProjectDetailsOverviewBlock projectUuid={projectUuid} index={index} />;
    case ProjectBlockKind.CLIENTS:
      return <ProjectDetailsClientsBlock projectUuid={projectUuid} index={index} />;
    case ProjectBlockKind.INTERNAL:
      return <ProjectDetailsInternalInformationBlock projectUuid={projectUuid} index={index} />;
    case ProjectBlockKind.STAFF:
      return <ProjectDetailsAssignedStaffBlock projectUuid={projectUuid} index={index} />;
    case ProjectBlockKind.MILESTONES:
      return <ProjectDetailsLeadInformationBlock projectUuid={projectUuid} index={index} />;
    case ProjectBlockKind.REFERRAL:
      return <ProjectDetailsReferralInformationBlock projectUuid={projectUuid} index={index} />;
    default:
      return null;
  }
};

const Block = memo(
  ({
    projectBlockKind,
    index,
    projectUuid,
    setProjectBlockKindToHeight,
  }: {
    projectBlockKind: ProjectBlockKindType;
    index: number;
    projectUuid: string;
    setProjectBlockKindToHeight: React.Dispatch<React.SetStateAction<{[key: string]: number}>>;
  }) => {
    return (
      <BlockContainer
        key={projectBlockKind}
        onLayout={(e) => {
          setProjectBlockKindToHeight((stateBlocks: {[key: string]: number}) => ({
            ...stateBlocks,
            [projectBlockKind]: e.nativeEvent.layout.height,
          }));
        }}
      >
        {index > 0 && <Line style={{height: 4}} />}
        <BlockContent kind={projectBlockKind} projectUuid={projectUuid} index={index} />
      </BlockContainer>
    );
  },
);

const SectionBlocks = ({
  projectUuid,
  block,
  blocks,
  handleUpdateBlock,
}: {
  projectUuid: string;
  block: ProjectBlockKindType;
  blocks: ProjectBlockKindType[];
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
}) => {
  const responsive = useResponsive();
  const flatList = useFlatList();
  const {
    projectBlockKindToHeight,
    setProjectBlockKindToHeight,
    isOnLoadScrollComplete,
    setIsOnLoadScrollComplete,
  } = useProjectDetailsContext();

  const {setPositionY, getBlockOfPositionY} = useProjectDetailsAutoScroll({
    block,
    allBlocks: blocks,
    flatList,
  });

  const handleOnScrollToIndexFailed = async ({index}: {index: number}) => {
    // Retry scrolling to the index
    await new Promise((resolve) => setTimeout(resolve, 500));
    flatList.handleScrollToIndex({index, animated: true});
  };

  const renderItem = useCallback(
    ({item: projectBlockKind, index}) => {
      return (
        <Block
          projectBlockKind={projectBlockKind}
          index={index}
          projectUuid={projectUuid}
          setProjectBlockKindToHeight={setProjectBlockKindToHeight}
        />
      );
    },
    [projectUuid, setProjectBlockKindToHeight],
  );

  const onScroll = useCallback(
    ({nativeEvent}: {nativeEvent: NativeScrollEvent}) => {
      const positionY = nativeEvent.contentOffset.y;
      setPositionY(positionY);

      const blockOfPositionY = getBlockOfPositionY(positionY);
      if (block !== blockOfPositionY) {
        handleUpdateBlock(blockOfPositionY || ProjectBlockKind.PROJECT_OVERVIEW);
      }

      if (!isOnLoadScrollComplete) {
        setIsOnLoadScrollComplete(true);
      }
    },
    [isOnLoadScrollComplete],
  );

  const getItemLayout = useCallback(
    (listBlocks: ProjectBlockKindType[] | null, index: number) => {
      if (!listBlocks) {
        return {
          length: 0,
          offset: 0,
          index,
        };
      }

      return {
        length: projectBlockKindToHeight[listBlocks[index]]
          ? projectBlockKindToHeight[listBlocks[index]]
          : 0,
        offset: _.sumBy(listBlocks.slice(0, index), (block) => projectBlockKindToHeight[block]),
        index,
      };
    },
    [projectBlockKindToHeight],
  );

  return (
    <FlatList
      listKey={'project-section-flat-list'}
      ref={flatList.ref}
      keyExtractor={(block) => block}
      data={blocks}
      style={{flex: 1, backgroundColor: colors.white}}
      initialNumToRender={blocks.length} // Render all items initially
      maxToRenderPerBatch={blocks.length} // Render all items per batch
      windowSize={blocks.length} // Render all items
      removeClippedSubviews={false}
      // Scroll events
      scrollEventThrottle={Infinity}
      onScrollToIndexFailed={handleOnScrollToIndexFailed}
      onScroll={onScroll}
      // Layout (for handling scroll to index)
      getItemLayout={getItemLayout}
      // Components
      ListFooterComponent={<Space height={responsive.desktop ? 32 : 8} />}
      renderItem={renderItem}
    />
  );
};

const ProjectDetailsWidgetSection = ({
  projectUuid,
  block,
  handleUpdateBlock,
  projectNavigationSections,
}: {
  projectUuid: string;
  block: ProjectBlockKindType;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  projectNavigationSections: NavigationSectionType[];
}) => {
  const blocks = useMemo(() => {
    return projectNavigationSections.reduce(
      (result: ProjectBlockKindType[], section) => [...result, ...section.blocks],
      [],
    );
  }, [projectNavigationSections]);

  return (
    <SectionBlocks
      projectUuid={projectUuid}
      block={block}
      blocks={blocks}
      handleUpdateBlock={handleUpdateBlock}
    />
  );
};

export default ProjectDetailsWidgetSection;
